import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useScrollRestoration } from "../hooks/useScrollRestoration";
import { useProducts } from "../hooks/useProducts";
import { productsApi } from "../api/products.api";
import { AssetStatusBadge } from "../components/Products/AssetStatusBadge";
import { GenerationProgress } from "../components/Products/GenerationProgress";
import { useBilling } from "../hooks/useBilling";
import { useToast } from "../hooks/useToast";
import { extractErrorMessage, toErrorMessage } from "../utils/errorMessage";
import { TableRowSkeleton } from "../components/ui/Skeleton";
import { QuotaCapModal } from "../components/Billing/QuotaCapModal";
import { TrialCountdownBanner } from "../components/Billing/TrialCountdownBanner";
import { useSettings } from "../hooks/useSettings";

export function ProductsPage() {
  useScrollRestoration();
  const [search, setSearch] = useState("");
  const [hasAssetFilter, setHasAssetFilter] = useState<"true" | "false" | undefined>();
  const [generatingProductId, setGeneratingProductId] = useState<string | null>(null);

  // Load shop settings (used by downstream hooks only)
  const { settings: shopSettings } = useSettings();
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (shopSettings && !defaultsAppliedRef.current) {
      defaultsAppliedRef.current = true;
    }
  }, [shopSettings]);
  const [showConfirmPanel, setShowConfirmPanel] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [selectedProductForGeneration, setSelectedProductForGeneration] = useState<string | null>(null);
  const navigate = useNavigate();
  
  const [isSyncing, setIsSyncing] = useState(false);

  // Fetch all products at once (100 covers any typical Shopify store).
  // Memoize params to prevent unnecessary re-renders.
  const productParams = useMemo(() => ({
    search: search || undefined,
    has_asset: hasAssetFilter,
    limit: 100,
  }), [search, hasAssetFilter]);
  
  const { products, loading, error, generateAsset, cancelGeneration, refetch } = useProducts(productParams);
  const { plan, trial } = useBilling();
  const { showError, showSuccess } = useToast();
  const [cancellingProductId, setCancellingProductId] = useState<string | null>(null);

  const hasActiveSubscription =
    plan?.subscription &&
    (plan.subscription.status === "active" || plan.subscription.status === "trialing");
  const canGenerate = hasActiveSubscription || (trial?.active && !trial?.used);

  const handleGenerateClick = useCallback(
    (productId: string) => {
      if (!canGenerate) {
        showError("You need an active subscription or free trial to generate AR models");
        return;
      }
      // Check quota cap
      const usage = plan?.usage;
      const planCode = plan?.plan?.code;
      if (
        usage &&
        planCode !== 'scale' &&
        planCode !== 'admin' &&
        usage.max_assets_per_month > 0 &&
        usage.assets_generated_this_month >= usage.max_assets_per_month
      ) {
        setShowQuotaModal(true);
        return;
      }
      setSelectedProductForGeneration(productId);
      setShowConfirmPanel(true);
    },
    [canGenerate, plan, showError]
  );

  const handleGenerateConfirm = useCallback(
    async () => {
      if (!selectedProductForGeneration) return;
      
      const productId = selectedProductForGeneration;
      setGeneratingProductId(productId);
      setShowConfirmPanel(false);
      
      try {
        await generateAsset(productId, {
          capture_mode: 'quick_capture',
          quality_mode: 'fast_render',
          view_mode: 'single',
        });
        showSuccess(`AR model generation started. This may take a few minutes.`);
        setTimeout(() => {
          setGeneratingProductId(null);
        }, 2000);
      } catch (err: any) {
        console.error("Failed to generate asset:", err);
        showError(extractErrorMessage(err, "Failed to generate AR model. Please try again."));
        setGeneratingProductId(null);
      } finally {
        setSelectedProductForGeneration(null);
      }
    },
    [generateAsset, selectedProductForGeneration, showError, showSuccess]
  );

  const handleGenerateCancel = useCallback(() => {
    setShowConfirmPanel(false);
    setSelectedProductForGeneration(null);
  }, []);

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const result = await productsApi.sync();
      showSuccess(`Synced ${result.synced} products (${result.created} new, ${result.updated} updated).`);
      await refetch();
    } catch (err: any) {
      showError(extractErrorMessage(err, "Sync failed. Please try again."));
    } finally {
      setIsSyncing(false);
    }
  }, [refetch, showError, showSuccess]);

  const handleCancelGeneration = useCallback(
    async (productId: string) => {
      if (!confirm("Are you sure you want to cancel the generation? This cannot be undone.")) {
        return;
      }
      setCancellingProductId(productId);
      try {
        await cancelGeneration(productId);
        showSuccess("Generation cancelled successfully.");
      } catch (err: any) {
        console.error("Failed to cancel generation:", err);
        showError(extractErrorMessage(err, "Failed to cancel generation. Please try again."));
      } finally {
        setCancellingProductId(null);
      }
    },
    [cancelGeneration, showError, showSuccess]
  );

  // Find product that's currently generating or processing
  const processingProduct = products?.data?.find(
    (p) => generatingProductId === p.id || p.asset?.status === "processing" || p.asset?.status === "pending"
  );

  return (
    <div className="page-grid">
      {/* Trial countdown - keeps the free-day window visible where generation happens */}
      <TrialCountdownBanner />

      {!canGenerate && (
        <div className="glass-panel border-amber-100 bg-amber-50/60 text-sm text-amber-700">
          {trial?.expired
            ? "Your free trial has expired. Subscribe to generate AR models."
            : "Subscribe to generate AR models for your products."}
        </div>
      )}

      {/* Generation confirm panel */}
      {showConfirmPanel && (
        <div className="glass-panel border border-indigo-200/70 bg-gradient-to-br from-indigo-50/85 via-sky-50/70 to-white space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Generate AR Model</h3>
            <p className="mt-1 text-sm text-slate-500">
              Open the product page to pick images and generation mode, or confirm here to start with the product's default photo.
            </p>
          </div>
          <div className="rounded-2xl border border-indigo-100 bg-white/70 px-4 py-3 space-y-1">
            <p className="text-sm text-slate-600">Single Reference Ultra — fastest setup using the main product photo.</p>
            <p className="text-xs text-slate-400">Estimated time: 5–15 minutes · Background auto-removed</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                if (selectedProductForGeneration) {
                  navigate(`/products/${selectedProductForGeneration}`);
                }
                setShowConfirmPanel(false);
              }}
              className="rounded-2xl border border-indigo-200 bg-indigo-50 px-5 py-2.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
            >
              Open product page
            </button>
            <button
              type="button"
              onClick={handleGenerateConfirm}
              disabled={!!generatingProductId}
              className="rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {generatingProductId ? "Generating..." : "Generate now"}
            </button>
            <button
              type="button"
              onClick={handleGenerateCancel}
              disabled={!!generatingProductId}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Quota cap modal */}
      <QuotaCapModal
        isOpen={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
      />

      {/* Show generation progress when a product is generating */}
      {processingProduct && (
        <GenerationProgress 
          productTitle={processingProduct.title}
          startedAt={processingProduct.asset?.generation_started_at}
          progressMessage={processingProduct.asset?.generation_progress_message}
          onCancel={() => handleCancelGeneration(processingProduct.id)}
          showCancel={!cancellingProductId}
          cancelButtonText="Cancel generation"
        />
      )}

      <div className="glass-panel">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex-1">
            <label className="text-sm font-semibold text-slate-700">
              Search products
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by product name..."
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setHasAssetFilter(hasAssetFilter === "true" ? undefined : "true")}
              className={`rounded-2xl px-3 py-2 text-xs font-semibold ${
                hasAssetFilter === "true"
                  ? "bg-indigo-500 text-white shadow-lg shadow-indigo-200"
                  : "border border-slate-200 text-slate-500"
              }`}
            >
              Has AR
            </button>
            <button
              type="button"
              onClick={() => setHasAssetFilter(hasAssetFilter === "false" ? undefined : "false")}
              className={`rounded-2xl px-3 py-2 text-xs font-semibold ${
                hasAssetFilter === "false"
                  ? "bg-indigo-500 text-white shadow-lg shadow-indigo-200"
                  : "border border-slate-200 text-slate-500"
              }`}
            >
              No AR
            </button>
            {hasAssetFilter && (
              <button
                type="button"
                onClick={() => setHasAssetFilter(undefined)}
                className="rounded-2xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={handleSync}
              disabled={isSyncing || loading}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSyncing ? "Syncing…" : "↻ Sync"}
            </button>
          </div>
        </div>
      </div>

      <div className="glass-panel overflow-hidden">
        {loading ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
              <thead>
                <tr>
                  {['Product', 'AR Status', 'Updated', 'Actions'].map((heading) => (
                    <th
                      key={heading}
                      className={`px-3 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 ${heading === 'Updated' ? 'hidden sm:table-cell' : ''}`}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRowSkeleton key={i} columns={4} />
                ))}
              </tbody>
            </table>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-100 bg-rose-50/60 px-4 py-3 text-sm text-rose-600">
            {toErrorMessage(error, "Failed to load products.")}
          </div>
        ) : !products || products.data.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-10 text-center">
            <h3 className="text-lg font-semibold text-slate-900">No products found</h3>
            <p className="mt-2 text-sm text-slate-500">Try adjusting your search or filters.</p>
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setHasAssetFilter(undefined);
              }}
              className="mt-4 rounded-2xl bg-white px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
              <thead>
                <tr>
                  {["Product", "AR Status", "Updated", "Actions"].map((heading) => (
                    <th key={heading} className={`px-3 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 ${heading === 'Updated' ? 'hidden sm:table-cell' : ''}`}>
                      {heading}
                    </th>
                ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.data.map((product) => {
                  const assetStatus = product.asset?.status || "none";
                  const isProcessing = assetStatus === "processing" || assetStatus === "pending";

                  return (
                    <tr key={product.id} className="hover:bg-slate-50/80">
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <img
                            src={product.primary_image_url || "https://images.unsplash.com/photo-1503602642458-232111445657?w=200"}
                            alt={product.title}
                            className="h-10 w-10 flex-shrink-0 rounded-xl object-cover"
                          />
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-900 max-w-[140px] sm:max-w-none">{product.title}</p>
                            <p className="text-xs text-slate-400 hidden sm:block">#{product.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <AssetStatusBadge status={assetStatus as any} />
                      </td>
                      <td className="px-3 py-3 text-slate-500 hidden sm:table-cell">
                        {new Date(product.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => navigate(`/products/${product.id}`)}
                            className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600"
                          >
                            View
                          </button>
                          {isProcessing ? (
                            <button
                              type="button"
                              onClick={() => handleCancelGeneration(product.id)}
                              disabled={!!cancellingProductId}
                              className={`rounded-xl px-3 py-1.5 text-xs font-semibold text-white transition ${
                                !!cancellingProductId
                                  ? "cursor-not-allowed bg-rose-300"
                                  : "bg-rose-500 shadow-lg shadow-rose-200 hover:bg-rose-600"
                              }`}
                            >
                              {cancellingProductId === product.id ? "Cancelling..." : "Cancel"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleGenerateClick(product.id)}
                              disabled={!canGenerate}
                              className={`rounded-xl px-3 py-1.5 text-xs font-semibold text-white transition ${
                                !canGenerate
                                  ? "cursor-not-allowed bg-slate-300"
                                  : "bg-gradient-to-r from-indigo-500 to-sky-500 shadow-lg shadow-indigo-200"
                              }`}
                            >
                              Generate AR
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
